import React, { useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@loader/ui';
import { Badge } from '@loader/ui';

import { DiffRow } from '@/lib/types';

interface DiffTableProps {
  data: DiffRow[];
  columns: string[];
  changeMeta?: Record<
    string,
    { added: number; updated: number; removed: number }
  >;
}

export const DiffTable = React.memo(
  ({ data, columns, changeMeta }: DiffTableProps) => {
    const tableColumns = useMemo<ColumnDef<DiffRow>[]>(() => {
      const cols: ColumnDef<DiffRow>[] = columns.map((col) => ({
        accessorKey: col,
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2">
            <span>{col}</span>
            {changeMeta?.[col] && (
              <div className="flex gap-1">
                {changeMeta[col].added > 0 && (
                  <Badge variant="success" className="text-xs">
                    +{changeMeta[col].added}
                  </Badge>
                )}
                {changeMeta[col].updated > 0 && (
                  <Badge variant="warning" className="text-xs">
                    ~{changeMeta[col].updated}
                  </Badge>
                )}
                {changeMeta[col].removed > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    -{changeMeta[col].removed}
                  </Badge>
                )}
              </div>
            )}
          </div>
        ),
        cell: ({ row, column }) => {
          const value = row.getValue(column.id);
          const diffType = row.original._diff_type;

          return (
            <div className={`relative ${getCellStyles(diffType)}`}>
              {diffType === 'added' && (
                <div className="absolute top-0 bottom-0 left-0 w-1 rounded-r bg-green-500" />
              )}
              {diffType === 'changed' && (
                <div className="absolute top-1 left-1 h-2 w-2 rounded-full bg-amber-500" />
              )}
              <span
                className={
                  diffType === 'removed' ? 'line-through opacity-50' : ''
                }
              >
                {value?.toString() ?? '-'}
              </span>
            </div>
          );
        },
      }));

      return cols;
    }, [columns, changeMeta]);

    const table = useReactTable({
      data,
      columns: tableColumns,
      getCoreRowModel: getCoreRowModel(),
    });

    return (
      <div className="rounded-md border bg-card shadow-sm">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
);

function getCellStyles(diffType?: 'added' | 'removed' | 'changed' | 'unchanged') {
  switch (diffType) {
    case 'added':
      return 'bg-green-500/5 border-l-4 border-border-success pl-4 dark:bg-green-500/10';
    case 'changed':
      return 'bg-amber-500/5 border-l-4 border-border-warning pl-4 dark:bg-amber-500/10';
    case 'removed':
      return 'bg-destructive/5 border-l-4 border-destructive pl-4 opacity-75 dark:bg-destructive/10';
    default:
      return '';
  }
}
